import { useEffect, useMemo, useRef, useState } from 'react'
import { ArrowDown, ArrowLeft, ArrowUp, Ban, CircleDollarSign, CircleHelp, Copy, FilePen, Inbox, Link2, Merge, Plus, RotateCcw, Save, Trash2, Upload, X, XCircle } from 'lucide-react'
import { cmsApi } from '../lib/api'
import { copyOf, flagUnchanged, newFaq, newRun, newSnapshot, plainTicketUrl } from '../lib/eventSnapshot'
import { formatWhen } from '../lib/format'
import { isLocalPreview, prepareImage, previewOf, storePrepared, type PreparedImage } from '../lib/images'
import { changedFaqs, newPlaceFaq, newPlaceForm, newProfileForm, storedPlaceForm, storedProfileForm, visibleFaqs as shownFaqs } from '../lib/catalog'
import type { BootstrapData, CatalogDraft, EventFaq, EventOccurrence, EventRun, EventSnapshot, EventStatus, MergeResult, OpenedEvent, PendingCatalog, Place, PlaceFaq, Producer } from '../types'
import { EventPreview } from './EventPreview'
import { EventReview } from './EventReview'
import { EventStatusDialog } from './EventStatusDialog'
import { ImageLinkInput } from './ImageLinkInput'
import { LeaveDialog } from './LeaveDialog'
import { LoadingState } from './LoadingState'
import { MergeDialog } from './MergeDialog'
import { PlaceQuickCreate, ProfileQuickCreate } from './QuickCreate'
import { EditedTag, isHandEdited } from './EditedTag'
import { RunSchedule } from './RunSchedule'
import { SuggestField } from './SuggestField'

type Props = {
  eventId: string | null
  // A draft of a new event, continued here (PRD.md, item 11).
  draftId: string | null
  // An event whose copy starts a new one (PRD.md, item 74).
  copyFrom: string | null
  // Where the CMS's menu asks the editor before leaving it (PRD.md, item 73).
  exit: { current: ((go: () => void) => void) | null }
  bootstrap: BootstrapData
  catalogEditing: boolean
  onBack: () => void
  onSaved: (message: string) => void
  onError: (message: string) => void
  // A message that leaves the editor open.
  onNotice: (message: string) => void
  // Another event, opened from the ones like this.
  onOpenEvent: (id: string) => void
  onMerged: (message: string) => void
  // A place or profile created from here reaches the lists at once.
  onCatalogChanged: () => Promise<void>
}

const normalizeMoney = (value: number | null) => value === null
  ? null
  : Math.round((value + Number.EPSILON) * 100) / 100

// What a draft keeps: the event as it stands, an image from a link by its link
// alone, prepared again when the draft opens.
const draftOf = (value: EventSnapshot): EventSnapshot => isLocalPreview(value.event.image_url)
  ? { ...value, event: { ...value.event, image_url: null } }
  : value

// An empty list as none, so a list emptied again leaves nothing unsaved.
const orNone = <T,>(items: T[] | undefined) => (items?.length ? items : undefined)

// The photo of a place's profile or a profile being created in the event.
const photoOf = (item: PendingCatalog) => item.type === 'place' ? item.value.profile.fields.image_url : item.value.image_url
const withPhoto = (item: PendingCatalog, url: string | null): PendingCatalog => item.type === 'place'
  ? { ...item, value: { ...item.value, profile: { ...item.value.profile, fields: { ...item.value.profile.fields, image_url: url } } } }
  : { ...item, value: { ...item.value, image_url: url } }

// An event as the editor works on it: each run with one list of profiles, the
// main producer first, as the app's "Com…", and the ticket link the app opens.
const openedOf = ({ calendar_adds: _calendarAdds, ...value }: OpenedEvent): EventSnapshot => ({
  ...value,
  runs: value.runs.map((run) => {
    const producerId = run.producer_id ?? value.event.producer_id ?? null
    return {
      ...run,
      producer_id: producerId,
      producer_name: run.producer_name ?? value.event.producer_name ?? '',
      credits: [...new Set([producerId, ...(run.credits ?? [])].filter((id): id is string => Boolean(id)))],
      ticket_url_source: plainTicketUrl(run.ticket_url) || run.ticket_url_source,
    }
  }),
  faqs: value.faqs ?? [],
})

const FESTIVAL = 'festival'

const hasAtMostTwoDecimalPlaces = (value: number) =>
  Math.abs(value * 100 - Math.round(value * 100)) < 1e-8

function normalizeBeforeSave(snapshot: EventSnapshot) {
  return {
    ...snapshot,
    event: {
      ...snapshot.event,
      title: snapshot.event.title.trim(),
      description: snapshot.event.description.trim(),
      producer_name: snapshot.event.producer_name.trim(),
      image_url_source: snapshot.event.image_url_source?.trim() || null,
      image_url: snapshot.event.image_url?.trim() || null,
    },
    runs: snapshot.runs.map((run) => {
      const activeDates = run.occurrences
        .filter((item) => item.status !== 'deleted' && item.starts_at)
        .map((item) => item.starts_at.slice(0, 10))
        .sort()
      return {
        ...run,
        place_name: run.place_name.trim(),
        producer_name: run.producer_name.trim(),
        // The main producer first, then the others, each once.
        credits: [...new Set([run.producer_id, ...(run.credits ?? [])].filter((id): id is string => Boolean(id)))],
        starts_on: activeDates[0] ?? run.starts_on,
        ends_on: activeDates.at(-1) ?? run.ends_on,
        is_free: Boolean(run.is_free),
        price_min: normalizeMoney(run.price_min),
        price_label: run.price_label.trim(),
        ticket_url_source: run.ticket_url_source?.trim() || null,
        ticket_url: run.ticket_url?.trim() || null,
        occurrences: run.occurrences.map((occurrence) => ({
          ...occurrence,
          price_min_override: normalizeMoney(occurrence.price_min_override),
        })),
      }
    }),
    faqs: snapshot.faqs.map((faq, index) => ({
      ...faq,
      question: faq.question.trim(),
      answer: faq.answer.trim(),
      sort_order: index,
    })),
    pending: orNone(snapshot.pending),
    place_faqs: orNone(snapshot.place_faqs?.map((faq) => ({ ...faq, question: faq.question.trim(), answer: faq.answer.trim() }))),
  }
}

// What goes to the app: the event, without what the editor keeps beside it.
const eventOnly = ({ pending: _pending, place_faqs: _placeFaqs, ...event }: EventSnapshot): EventSnapshot => event

export function EventEditor({ eventId, draftId, copyFrom, exit, bootstrap, catalogEditing, onBack, onSaved, onError, onNotice, onOpenEvent, onMerged, onCatalogChanged }: Props) {
  const [snapshot, setSnapshot] = useState<EventSnapshot | null>(null)
  // The event as it opened, to send only what the editor changed.
  const [original, setOriginal] = useState<EventSnapshot | null>(null)
  const [calendarAdds, setCalendarAdds] = useState<Record<string, number>>({})
  const [loading, setLoading] = useState(Boolean(eventId || draftId || copyFrom))
  const [saving, setSaving] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [imageMode, setImageMode] = useState<'link' | 'upload'>('link')
  const [uploadLabel, setUploadLabel] = useState('')
  // A link typed and not yet copied to the Guve, or being copied.
  const [linkPending, setLinkPending] = useState(false)
  // The new cover, prepared in the browser and stored in the Guve on save.
  const [cover, setCover] = useState<PreparedImage | null>(null)
  const [pageError, setPageError] = useState('')
  const [merging, setMerging] = useState(false)
  // Cancel, delete or republish the whole event, asked first (PRD.md, item 36).
  const [confirming, setConfirming] = useState<EventStatus | null>(null)
  // Publishing or saving shows what goes out first (PRD.md, item 46); going
  // back returns to where the form was.
  const [reviewing, setReviewing] = useState(false)
  const formScroll = useRef(0)
  // The place or profile being created whose window is open; the others wait
  // in their run as "em criação" (PRD.md, item 98).
  const [openPending, setOpenPending] = useState<string | null>(null)
  // Places and profiles begun in Lugares and Perfis, to continue here.
  const [catalogDrafts, setCatalogDrafts] = useState<{ place: CatalogDraft[]; producer: CatalogDraft[] }>({ place: [], producer: [] })
  // The questions of the event's places as saved, loaded as places are chosen
  // (PRD.md, item 99); the editor's changes wait in snapshot.place_faqs.
  const [placeFaqsSaved, setPlaceFaqsSaved] = useState<PlaceFaq[]>([])
  const loadedPlaces = useRef(new Set<string>())
  // A new event's draft: its id from the first save, what it holds, the save
  // under way and the last one's result. Closed once published or discarded.
  const draft = useRef<string | null>(draftId)
  const drafted = useRef('')
  const drafting = useRef<Promise<boolean> | null>(null)
  const draftClosed = useRef(false)
  // A photo file goes to the Guve once, on the draft's first save after it.
  const storedCovers = useRef(new Map<string, string>())
  const pendingDraft = useRef<(() => void) | null>(null)
  const [draftState, setDraftState] = useState<{ status: 'idle' | 'saving' | 'saved' | 'error'; at?: string }>({ status: 'idle' })
  // A new event as it started, blank or a copy, to tell whether it has
  // anything in it; and the way out waiting for an answer (PRD.md, item 73).
  const started = useRef('')
  // The event a copy comes from, said on top so duplicating is never mistaken
  // for opening it (PRD.md, item 74).
  const [copiedTitle, setCopiedTitle] = useState('')
  // An event imported from a link (PRD.md, item 93): its page, to check
  // against, and what the import could not tell for sure.
  const [imported, setImported] = useState<{ sourceUrl: string | null; warnings: string[] } | null>(null)
  const [leaving, setLeaving] = useState<(() => void) | null>(null)

  // By its name, not the list: reloading the lists after a place or profile is
  // created gives a new list, and a new event must not start over.
  const firstCategory = bootstrap.categories[0]?.name ?? ''
  useEffect(() => {
    if (!eventId && draftId) {
      setLoading(true)
      cmsApi.getDraft(draftId)
        .then((value) => {
          drafted.current = JSON.stringify(value.snapshot)
          setSnapshot(value.snapshot)
          setDraftState({ status: 'saved', at: value.updated_at })
          setImageMode(value.snapshot.event.image_url && !value.snapshot.event.image_url_source ? 'upload' : 'link')
          if (value.origin === 'import') setImported({ sourceUrl: value.source_url ?? null, warnings: value.warnings ?? [] })
        })
        .catch((error) => setPageError(error instanceof Error ? error.message : 'Não foi possível abrir o rascunho.'))
        .finally(() => setLoading(false))
      return
    }
    if (!eventId && copyFrom) {
      setLoading(true)
      cmsApi.getEvent(copyFrom)
        .then((value) => {
          const copy = copyOf(openedOf(value))
          started.current = drafted.current = JSON.stringify(copy)
          setSnapshot(copy)
          setCopiedTitle(value.event.title)
          setImageMode(copy.event.image_url ? 'upload' : 'link')
        })
        .catch((error) => setPageError(error instanceof Error ? error.message : 'Não foi possível duplicar o evento.'))
        .finally(() => setLoading(false))
      return
    }
    if (!eventId) {
      const initial = newSnapshot(firstCategory)
      started.current = drafted.current = JSON.stringify(initial)
      setSnapshot(initial)
      setLoading(false)
      return
    }
    setLoading(true)
    cmsApi.getEvent(eventId)
      .then((value) => {
        const opened = openedOf(value)
        setSnapshot(opened)
        setOriginal(opened)
        setCalendarAdds(value.calendar_adds ?? {})
        setImageMode(value.event.image_url_source ? 'link' : 'upload')
      })
      .catch((error) => setPageError(error instanceof Error ? error.message : 'Não foi possível abrir o evento.'))
      .finally(() => setLoading(false))
  }, [firstCategory, eventId, draftId, copyFrom])


  // Saves the draft of a new event, one save after another, as Gmail does:
  // automatically after each change, on leaving and on the button (forced).
  const saveDraft = (value: EventSnapshot, force = false): Promise<boolean> => {
    const run = async () => {
      if (draftClosed.current) return true
      let content = draftOf(value)
      const preview = value.event.image_url
      if (cover && preview && isLocalPreview(preview) && !value.event.image_url_source) {
        setDraftState({ status: 'saving' })
        const stored = storedCovers.current.get(preview) ?? await storePrepared(cover, 'event')
        storedCovers.current.set(preview, stored)
        content = { ...value, event: { ...value.event, image_url: stored } }
        setSnapshot((current) => current && current.event.image_url === preview ? { ...current, event: { ...current.event, image_url: stored } } : current)
      }
      // The photos of places and profiles being created, too (PRD.md, item 98).
      const pending = content.pending ?? []
      const kept = await Promise.all(pending.map(async (item): Promise<PendingCatalog> => {
        const stored = item.type === 'place' ? await storedPlaceForm(item.value) : await storedProfileForm(item.value)
        return stored === item.value ? item : { ...item, value: stored } as PendingCatalog
      }))
      if (kept.some((item, index) => item !== pending[index])) {
        const moved = new Map(pending.map((item, index) => [photoOf(item), photoOf(kept[index])]))
        content = { ...content, pending: kept }
        setSnapshot((current) => current && {
          ...current,
          pending: current.pending?.map((item) => moved.has(photoOf(item)) && isLocalPreview(photoOf(item)) ? withPhoto(item, moved.get(photoOf(item)) ?? null) : item),
        })
      }
      const json = JSON.stringify(content)
      if (!force && json === drafted.current) return true
      draft.current ??= crypto.randomUUID()
      setDraftState({ status: 'saving' })
      const saved = await cmsApi.saveDraft(draft.current, content)
      drafted.current = json
      setDraftState({ status: 'saved', at: saved.updated_at })
      return true
    }
    const next = (drafting.current ?? Promise.resolve(true)).then(run).catch((error) => {
      if (!draftClosed.current) setDraftState({ status: 'error' })
      if (force) onError(error instanceof Error ? error.message : 'Não foi possível salvar o rascunho.')
      return false
    })
    drafting.current = next
    return next
  }

  useEffect(() => {
    if (eventId || !snapshot) return
    const flush = () => { pendingDraft.current = null; void saveDraft(snapshot) }
    pendingDraft.current = flush
    const timer = window.setTimeout(flush, 2000)
    return () => window.clearTimeout(timer)
  }, [snapshot])
  // Leaving the editor saves what the last change left waiting.
  useEffect(() => () => pendingDraft.current?.(), [])

  // Places and profiles begun in Lugares and Perfis, offered in the fields.
  useEffect(() => {
    if (!catalogEditing) return
    Promise.all([cmsApi.listCatalogDrafts('place'), cmsApi.listCatalogDrafts('producer')])
      .then(([place, producer]) => setCatalogDrafts({ place, producer }))
      .catch(() => {})
  }, [catalogEditing])

  // The questions of each place chosen, once (PRD.md, item 99).
  const placeIds = [...new Set((snapshot?.runs ?? []).filter((run) => run.status !== 'deleted' && run.place_id).map((run) => run.place_id!))].sort()
  const placeKey = placeIds.join(',')
  useEffect(() => {
    const missing = placeIds.filter((id) => !loadedPlaces.current.has(id))
    if (!missing.length) return
    missing.forEach((id) => loadedPlaces.current.add(id))
    cmsApi.placeFaqs(missing)
      .then((items) => setPlaceFaqsSaved((current) => [...current, ...items]))
      .catch(() => {
        missing.forEach((id) => loadedPlaces.current.delete(id))
        onError('Não foi possível carregar as perguntas dos locais.')
      })
  }, [placeKey])

  const savedSessions = useMemo(() => new Set(original?.runs.flatMap((run) => run.occurrences.map((item) => item.id)) ?? []), [original])

  // What the place and profile fields suggest as they are typed (PRD.md,
  // item 41): a place with its city and its profile's @, found by either
  // name ("MAM" finds the Museu de Arte Moderna); a profile with its kind.
  // A draft of Lugares or Perfis is found too, to continue here, never chosen
  // as it is (PRD.md, item 98).
  const draftSuggestions = (drafts: CatalogDraft[]) => drafts.map((draft) => ({
    id: `draft:${draft.id}`, label: draft.title || 'Sem nome', detail: 'Rascunho · continuar aqui',
  }))
  const placeSuggestions = useMemo(() => {
    const venues = new Map(bootstrap.producers.filter((item) => item.kind === 'venue' && item.place_id).map((item) => [item.place_id, item]))
    return [...bootstrap.places.map((place) => {
      const venue = venues.get(place.id)
      return {
        id: place.id,
        label: place.name,
        detail: [place.city, venue?.handle && `@${venue.handle}`].filter(Boolean).join(' · '),
        words: [place.city, venue?.name, venue?.handle].filter(Boolean).join(' '),
      }
    }), ...draftSuggestions(catalogDrafts.place)]
  }, [bootstrap.places, bootstrap.producers, catalogDrafts.place])
  const profileSuggestions = useMemo(() => bootstrap.producers
    .filter((item) => (item.kind ?? 'producer') !== 'venue')
    .map((item) => ({
      id: item.id,
      label: item.name,
      detail: [item.kind === 'artist' ? 'Artista' : 'Produtor', item.handle && `@${item.handle}`].filter(Boolean).join(' · '),
      words: item.handle ?? '',
    })), [bootstrap.producers])

  // Something not saved yet, as Shopify's save bar shows: a new event always;
  // an open one once it differs from how it opened, or a photo link waits.
  const unsaved = useMemo(() => !original || !snapshot
    || JSON.stringify(normalizeBeforeSave(snapshot)) !== JSON.stringify(normalizeBeforeSave(original)), [original, snapshot])

  // Closing the tab with work not yet saved asks first, as Gmail does: a new
  // event's last change before its draft saves, or an open event's changes.
  useEffect(() => {
    const waiting = !snapshot ? false
      : eventId ? unsaved || linkPending
        : draftState.status === 'saving' || draftState.status === 'error' || JSON.stringify(draftOf(snapshot)) !== drafted.current
    if (!waiting) return
    const warn = (event: BeforeUnloadEvent) => {
      event.preventDefault()
      event.returnValue = ''
    }
    window.addEventListener('beforeunload', warn)
    return () => window.removeEventListener('beforeunload', warn)
  }, [eventId, snapshot, unsaved, linkPending, draftState.status])

  // Leaving with something pending asks first (PRD.md, item 73): an open
  // event's unsaved changes, or a new event with something in it. A draft
  // opened from the list saves on leaving, as Gmail does.
  const asksOnLeave = !snapshot ? false
    : eventId ? unsaved || linkPending
      : !draftId && (draft.current !== null || JSON.stringify(snapshot) !== started.current)
  const leave = (go: () => void) => (asksOnLeave ? setLeaving(() => go) : go())
  useEffect(() => {
    exit.current = leave
    return () => { exit.current = null }
  })

  if (loading) return <LoadingState label="Abrindo evento…" />
  if (!snapshot || pageError) return <div className="error-state"><p>{pageError || 'Evento não encontrado.'}</p><button className="button button--secondary" onClick={onBack}>Voltar</button></div>

  const updateEvent = <K extends keyof EventSnapshot['event']>(key: K, value: EventSnapshot['event'][K]) => {
    setSnapshot((current) => current ? { ...current, event: { ...current.event, [key]: value } } : current)
  }

  const categorySlugOf = (name: string) => bootstrap.categories.find((category) => category.name === name)?.slug
  const isFestival = categorySlugOf(snapshot.event.category) === FESTIVAL

  // Only a festival appears in other categories (PRD.md, item 123): leaving
  // Festival takes them away, and the review shows it.
  const changeCategory = (category: string) => {
    setSnapshot((current) => current && {
      ...current,
      event: { ...current.event, category },
      ...(categorySlugOf(category) === FESTIVAL ? {} : { extra_category_slugs: [] }),
    })
  }

  const toggleExtraCategory = (slug: string) => {
    setSnapshot((current) => {
      if (!current) return current
      const extras = current.extra_category_slugs ?? []
      return { ...current, extra_category_slugs: extras.includes(slug) ? extras.filter((item) => item !== slug) : [...extras, slug].sort() }
    })
  }

  const updateRun = (runId: string, patch: Partial<EventRun>) => {
    setSnapshot((current) => current ? { ...current, runs: current.runs.map((run) => run.id === runId ? { ...run, ...patch } : run) } : current)
  }

  const updateOccurrence = (runId: string, occurrenceId: string, patch: Partial<EventOccurrence>) => {
    setSnapshot((current) => current ? {
      ...current,
      runs: current.runs.map((run) => run.id === runId ? {
        ...run,
        occurrences: run.occurrences.map((occurrence) => occurrence.id === occurrenceId ? { ...occurrence, ...patch } : occurrence),
      } : run),
    } : current)
  }

  const addRun = () => setSnapshot((current) => current ? { ...current, runs: [...current.runs, newRun(current.event.id)] } : current)

  const setOccurrences = (runId: string, occurrences: EventOccurrence[]) => updateRun(runId, { occurrences })

  // Canceled at one place: the app shows its sessions there as canceled,
  // the others as they are.
  const toggleRunCanceled = (run: EventRun) => {
    const canceling = run.status !== 'canceled'
    const place = run.place_name || 'este local'
    if (canceling && !window.confirm(`Cancelar em ${place}? Os horários daqui aparecem como cancelados no app; os outros locais seguem normais. Salve o evento para publicar.`)) return
    updateRun(run.id, { status: canceling ? 'canceled' : 'published' })
  }

  // A new event on the text, photo and settings of one that exists.
  const merged = (result: MergeResult, mergedTitle: string) => {
    setMerging(false)
    const sessions = result.sessions_moved + result.sessions_joined
    onMerged(`“${mergedTitle}” entrou neste evento: ${sessions} ${sessions === 1 ? 'horário' : 'horários'} e ${result.saves} ${result.saves === 1 ? 'favorito' : 'favoritos'}.`)
  }

  // A new photo starts centered, in the card and on the page.
  const setImage = (url: string | null, source: string | null) => setSnapshot((current) => current ? {
    ...current,
    event: { ...current.event, image_url: url, image_url_source: source,
      image_focus_x: null, image_focus_y: null, image_page_focus_x: null, image_page_focus_y: null },
  } : current)

  // A run's profiles (PRD.md, item 13), in the order of the app's "Com…"
  // line; the first is the main producer, as cms_set_run_credits keeps it.
  const updateRunWith = (runId: string, change: (run: EventRun) => Partial<EventRun>) => setSnapshot((current) => current ? {
    ...current,
    runs: current.runs.map((run) => run.id === runId ? { ...run, ...change(run) } : run),
  } : current)
  const withProfiles = (ids: string[], named?: Pick<Producer, 'id' | 'name'> | null): Partial<EventRun> => ({
    credits: ids,
    producer_id: ids[0] ?? null,
    producer_name: !ids[0] ? '' : ids[0] === named?.id ? named.name : bootstrap.producers.find((item) => item.id === ids[0])?.name ?? '',
  })
  // The place's own profile, which comes and goes with the place.
  const venueOf = (placeId: string | null) => placeId
    ? bootstrap.producers.find((item) => item.kind === 'venue' && item.place_id === placeId) ?? null
    : null
  // A place, its profile first and the last place's out; the rest stay.
  const choosePlace = (runId: string, place: Place | null, venue: Pick<Producer, 'id' | 'name'> | null) => updateRunWith(runId, (run) => {
    const previous = venueOf(run.place_id)?.id
    const others = (run.credits ?? []).filter((id) => id !== previous && id !== venue?.id)
    return { place_id: place?.id ?? null, place_name: place?.name ?? '', ...withProfiles(venue ? [venue.id, ...others] : others, venue) }
  })

  const addProfile = (runId: string, producerId: string) => {
    if (producerId.startsWith('draft:')) { void continueDraft(runId, 'profile', producerId.slice(6)); return }
    updateRunWith(runId, (run) => withProfiles([...new Set([...(run.credits ?? []), producerId])]))
  }
  const removeProfile = (runId: string, producerId: string) =>
    updateRunWith(runId, (run) => withProfiles((run.credits ?? []).filter((id) => id !== producerId)))

  // Places and profiles being created here (PRD.md, item 98): kept with the
  // event and its draft until created or discarded; one place a run.
  const pending = snapshot.pending ?? []
  const setPending = (change: (items: PendingCatalog[]) => PendingCatalog[]) =>
    setSnapshot((current) => current ? { ...current, pending: orNone(change(current.pending ?? [])) } : current)
  const startPending = (item: PendingCatalog) => {
    const previous = item.type === 'place' && pending.find((other) => other.type === 'place' && other.run_id === item.run_id)
    if (previous && !window.confirm(`Descartar ${previous.value.name || 'o lugar'} em criação neste local?`)) return
    setPending((items) => [...items.filter((other) => other !== previous), item])
    setOpenPending(item.id)
  }
  const createPlace = (runId: string, name: string) =>
    startPending({ id: crypto.randomUUID(), run_id: runId, type: 'place', value: newPlaceForm(name) })
  const createProfile = (runId: string, name: string) =>
    startPending({ id: crypto.randomUUID(), run_id: runId, type: 'profile', value: newProfileForm(name) })
  // A draft of Lugares or Perfis continues here; creating it closes the draft.
  const continueDraft = async (runId: string, type: 'place' | 'profile', draftId: string) => {
    try {
      const { content } = await cmsApi.getCatalogDraft<PendingCatalog['value']>(draftId)
      startPending({ id: crypto.randomUUID(), run_id: runId, type, draft_id: draftId, value: content } as PendingCatalog)
    } catch (error) {
      onError(error instanceof Error ? error.message : 'Não foi possível abrir o rascunho.')
    }
  }
  const updatePending = (id: string, value: PendingCatalog['value']) =>
    setPending((items) => items.map((item) => item.id === id ? { ...item, value } as PendingCatalog : item))
  const discardPending = (id: string) => {
    setPending((items) => items.filter((item) => item.id !== id))
    setOpenPending(null)
  }
  // Created: out of the run, and its draft, closed, out of the suggestions.
  const forget = (item: PendingCatalog) => {
    discardPending(item.id)
    const kind = item.type === 'place' ? 'place' : 'producer'
    if (item.draft_id) setCatalogDrafts((current) => ({ ...current, [kind]: current[kind].filter((draft) => draft.id !== item.draft_id) }))
  }

  const placeCreated = async (item: PendingCatalog, place: Place, profile: Pick<Producer, 'id' | 'name'> | null) => {
    forget(item)
    await onCatalogChanged()
    choosePlace(item.run_id, place, profile)
    onNotice(`${place.name} criado e escolhido no evento.`)
  }

  const profileCreated = async (item: PendingCatalog, profile: Pick<Producer, 'id' | 'name'>) => {
    forget(item)
    await onCatalogChanged()
    updateRunWith(item.run_id, (run) => withProfiles([...new Set([...(run.credits ?? []), profile.id])], profile))
    onNotice(`Perfil ${profile.name} criado e incluído no evento.`)
  }

  const selectPlace = (runId: string, placeId: string) => {
    if (placeId.startsWith('draft:')) { void continueDraft(runId, 'place', placeId.slice(6)); return }
    const place = bootstrap.places.find((item) => item.id === placeId) ?? null
    choosePlace(runId, place, venueOf(place?.id ?? null))
  }

  // A profile becomes its place's own (PRD.md, item 42), after confirming: it
  // comes first here and joins the place's next events by itself. The whole
  // profile is sent back, so nothing else in it changes.
  const linkVenue = async (runId: string, placeId: string, producerId: string) => {
    const place = bootstrap.places.find((item) => item.id === placeId)
    const profile = bootstrap.producers.find((item) => item.id === producerId)
    if (!place || !profile) return
    if (!window.confirm(`Vincular ${profile.name} como o perfil de ${place.name}? Ele passa a entrar sozinho nos eventos deste local.`)) return
    try {
      const { producer } = await cmsApi.getProducer(producerId)
      await cmsApi.saveProducer({ ...producer, kind: 'venue', place_id: placeId })
      await onCatalogChanged()
      choosePlace(runId, place, profile)
      onNotice(`${profile.name} agora é o perfil de ${place.name}.`)
    } catch (error) {
      onError(error instanceof Error ? error.message : 'Não foi possível vincular o perfil.')
    }
  }

  const updateFaq = (faqId: string, patch: Partial<EventFaq>) => {
    setSnapshot((current) => current ? {
      ...current,
      faqs: current.faqs.map((faq) => faq.id === faqId ? { ...faq, ...patch } : faq),
    } : current)
  }

  const visibleFaqs = snapshot.faqs.filter((faq) => faq.status !== 'deleted')
  const visibleRuns = snapshot.runs.filter((run) => run.status !== 'deleted')

  // Removing a place just removes it, after confirming, as its sessions go too
  // (PRD.md, item 28): a new one leaves; a published one leaves the screen and
  // is deleted on saving. Descartar alterações undoes both.
  const removeRun = (run: EventRun) => {
    const saved = original?.runs.some((item) => item.id === run.id)
    // A published place that won't happen is canceled: removing tells no one
    // who put its sessions in their calendar (PRD.md, item 107).
    if (!window.confirm(`Remover ${run.place_name || 'este local'} do evento? Os horários dele saem junto.${saved ? ' Se não vai mais acontecer, use Cancelar neste local: quem pôs um horário na agenda é avisado. Para desfazer antes de salvar, use Descartar alterações.' : ''}`)) return
    setSnapshot((current) => current ? {
      ...current,
      runs: saved
        ? current.runs.map((item) => item.id === run.id ? { ...item, status: 'deleted' } : item)
        : current.runs.filter((item) => item.id !== run.id),
      pending: orNone(current.pending?.filter((item) => item.run_id !== run.id)),
    } : current)
  }

  const addFaq = () => setSnapshot((current) => current ? {
    ...current,
    faqs: [...current.faqs, newFaq(current.event.id, current.faqs.length)],
  } : current)

  // Among the questions in view; the removed ones wait at the end.
  const moveFaq = (faqId: string, direction: -1 | 1) => {
    setSnapshot((current) => {
      if (!current) return current
      const visible = current.faqs.filter((faq) => faq.status !== 'deleted')
      const index = visible.findIndex((faq) => faq.id === faqId)
      const destination = index + direction
      if (index < 0 || destination < 0 || destination >= visible.length) return current
      const [faq] = visible.splice(index, 1)
      visible.splice(destination, 0, faq)
      return { ...current, faqs: [...visible, ...current.faqs.filter((item) => item.status === 'deleted')] }
    })
  }

  // Removing just removes (PRD.md, item 21): a new question leaves; a published
  // one leaves the screen and is deleted on saving. Descartar alterações undoes both.
  const removeFaq = (faqId: string) => setSnapshot((current) => {
    if (!current) return current
    const saved = original?.faqs.some((faq) => faq.id === faqId)
    const others = current.faqs.filter((faq) => faq.id !== faqId)
    const faq = current.faqs.find((item) => item.id === faqId)
    return { ...current, faqs: saved && faq ? [...others, { ...faq, status: 'deleted' }] : others }
  })

  // The questions of the event's places (PRD.md, item 99): as saved, with the
  // changes made here on top, saved with the event one by one.
  const placeNameOf = (placeId: string) => bootstrap.places.find((place) => place.id === placeId)?.name ?? 'este local'
  const placeFaqsOf = (placeId: string) => {
    const edits = (snapshot.place_faqs ?? []).filter((faq) => faq.place_id === placeId)
    const saved = placeFaqsSaved.filter((faq) => faq.place_id === placeId)
    return [...saved.map((faq) => edits.find((edit) => edit.id === faq.id) ?? faq), ...edits.filter((edit) => !saved.some((faq) => faq.id === edit.id))]
      .sort((left, right) => left.sort_order - right.sort_order)
  }
  const setPlaceFaqs = (placeId: string, next: PlaceFaq[]) => setSnapshot((current) => current ? {
    ...current,
    place_faqs: orNone([
      ...(current.place_faqs ?? []).filter((faq) => faq.place_id !== placeId),
      ...changedFaqs(next, placeFaqsSaved.filter((faq) => faq.place_id === placeId), false),
    ]),
  } : current)
  const updatePlaceFaq = (faq: PlaceFaq, patch: Partial<PlaceFaq>) =>
    setPlaceFaqs(faq.place_id, placeFaqsOf(faq.place_id).map((item) => item.id === faq.id ? { ...item, ...patch } : item))
  const movePlaceFaq = (faq: PlaceFaq, direction: -1 | 1) => {
    const all = placeFaqsOf(faq.place_id)
    const shown = shownFaqs(all)
    const index = shown.findIndex((item) => item.id === faq.id)
    const destination = index + direction
    if (index < 0 || destination < 0 || destination >= shown.length) return
    const [item] = shown.splice(index, 1)
    shown.splice(destination, 0, item)
    setPlaceFaqs(faq.place_id, [...shown, ...all.filter((other) => other.status === 'deleted')])
  }
  // A saved one leaves every event of the place, so it asks first.
  const removePlaceFaq = (faq: PlaceFaq, ask = true) => {
    if (ask && placeFaqsSaved.some((item) => item.id === faq.id)
      && !window.confirm(`Remover esta pergunta de todos os eventos de ${placeNameOf(faq.place_id)}? Ela sai ao salvar o evento.`)) return
    setPlaceFaqs(faq.place_id, placeFaqsOf(faq.place_id).map((item) => item.id === faq.id ? { ...item, status: 'deleted' } : item))
  }

  // Where a question applies: this event, at all its places or at one, or
  // every event at a place, where it then moves.
  const scopeOf = (faq: EventFaq | PlaceFaq) => 'place_id' in faq ? `place:${faq.place_id}` : faq.event_run_id ? `run:${faq.event_run_id}` : 'event'
  const changeScope = (faq: EventFaq | PlaceFaq, scope: string) => {
    if (scope === scopeOf(faq)) return
    if ('place_id' in faq) {
      if (placeFaqsSaved.some((item) => item.id === faq.id)
        && !window.confirm(`Esta pergunta sai de todos os eventos de ${placeNameOf(faq.place_id)} ao salvar o evento. Continuar?`)) return
      removePlaceFaq(faq, false)
    } else if (scope.startsWith('place:')) {
      removeFaq(faq.id)
    } else {
      updateFaq(faq.id, { event_run_id: scope.startsWith('run:') ? scope.slice(4) : null })
      return
    }
    const copied = { question: faq.question, answer: faq.answer }
    if (scope.startsWith('place:')) {
      const placeId = scope.slice(6)
      const list = placeFaqsOf(placeId)
      setPlaceFaqs(placeId, [...list, { ...newPlaceFaq(placeId, shownFaqs(list).length), ...copied }])
    } else {
      setSnapshot((current) => current ? {
        ...current,
        faqs: [...current.faqs, { ...newFaq(current.event.id, current.faqs.length), event_run_id: scope.startsWith('run:') ? scope.slice(4) : null, ...copied }],
      } : current)
    }
  }

  const uploadImage = async (file: File | undefined) => {
    if (!file) return
    setUploading(true)
    try {
      const prepared = await prepareImage(file)
      setImage(await previewOf(prepared), null)
      setCover(prepared)
      setUploadLabel(`${prepared.label} · vai para o Guve quando você salvar`)
    } catch (error) {
      onError(error instanceof Error ? error.message : 'Não foi possível preparar a imagem.')
    } finally {
      setUploading(false)
    }
  }

  // The questions changed here of the places the event still has.
  const placeFaqsToSave = (value: EventSnapshot) => {
    const places = new Set(value.runs.filter((run) => run.status !== 'deleted' && run.place_id).map((run) => run.place_id))
    return (value.place_faqs ?? [])
      .filter((faq) => places.has(faq.place_id))
      .map((faq) => ({ ...faq, question: faq.question.trim(), answer: faq.answer.trim() }))
  }

  const validate = (value: EventSnapshot) => {
    if (!value.event.title.trim()) return 'Informe o título do evento.'
    if (!value.event.category) return 'Escolha uma categoria.'
    const activeRuns = value.runs.filter((run) => run.status !== 'deleted')
    if (!activeRuns.length) return 'Mantenha ao menos um local/temporada ativo.'
    // What is being created here is created or discarded first (PRD.md, item 98).
    const waiting = (value.pending ?? []).find((item) => activeRuns.some((run) => run.id === item.run_id))
    if (waiting) {
      return `Termine de criar ${waiting.type === 'place' ? '' : 'o perfil '}${waiting.value.name || (waiting.type === 'place' ? 'o lugar' : '')} (Continuar) ou descarte, antes de ${eventId ? 'salvar' : 'publicar'}.`
    }
    for (const run of activeRuns) {
      if (!run.place_id) return 'Escolha um local em todas as temporadas ativas.'
      const activeOccurrences = run.occurrences.filter((item) => item.status !== 'deleted')
      if (!activeOccurrences.length) return `Inclua ao menos uma ocorrência em ${run.place_name || 'cada local'}.`
      if (
        run.price_min !== null
        && (!Number.isFinite(run.price_min) || run.price_min < 0 || !hasAtMostTwoDecimalPlaces(run.price_min))
      ) return 'O preço mínimo deve ser zero ou maior e ter no máximo duas casas decimais.'
      if (activeOccurrences.some((item) => (
        item.price_min_override !== null
        && (
          !Number.isFinite(item.price_min_override)
          || item.price_min_override < 0
          || !hasAtMostTwoDecimalPlaces(item.price_min_override)
        )
      ))) return 'O preço de uma ocorrência deve ser zero ou maior e ter no máximo duas casas decimais.'
      if (activeOccurrences.some((item) => !item.starts_at)) return 'Informe a data e a hora de todas as ocorrências ativas.'
    }
    const activeFaqs = value.faqs.filter((faq) => faq.status !== 'deleted')
    for (const faq of activeFaqs) {
      if (!faq.question.trim()) return 'Informe a pergunta em todos os itens de FAQ ativos.'
      if (!faq.answer.trim()) return 'Informe a resposta em todos os itens de FAQ ativos.'
      if (faq.event_run_id && !activeRuns.some((run) => run.id === faq.event_run_id)) {
        return 'Associe cada FAQ a um local ativo ou a todos os locais.'
      }
    }
    for (const faq of placeFaqsToSave(value)) {
      if (faq.status !== 'deleted' && (!faq.question || !faq.answer)) return 'Informe a pergunta e a resposta em todas as perguntas dos locais.'
    }
    return ''
  }

  const problemOf = (clean: EventSnapshot) => {
    const linkWaiting = (imageMode === 'link' && linkPending) || (!eventId && Boolean(clean.event.image_url_source) && !clean.event.image_url)
    return linkWaiting
      ? 'A imagem do link ainda não foi preparada: toque em Buscar imagem e espere ficar pronta.'
      : uploading ? 'Espere a imagem terminar de ser preparada.' : validate(clean)
  }

  // The form's errors come before the review, as they did before saving.
  const review = () => {
    const problem = problemOf(normalizeBeforeSave(snapshot))
    if (problem) { onError(problem); return }
    formScroll.current = window.scrollY
    setReviewing(true)
    window.scrollTo(0, 0)
  }
  const backToForm = () => {
    setReviewing(false)
    requestAnimationFrame(() => window.scrollTo(0, formScroll.current))
  }

  const save = async () => {
    const clean = normalizeBeforeSave(snapshot)
    const validationError = problemOf(clean)
    if (validationError) {
      onError(validationError)
      return
    }
    setSaving(true)
    try {
      // A draft save under way finishes first, so the server closes the draft
      // it has; none follows.
      await drafting.current
      draftClosed.current = true
      // The new cover reaches the Guve now, and the event points to it.
      if (cover && isLocalPreview(clean.event.image_url)) {
        clean.event.image_url = await storePrepared(cover, 'event')
      }
      await cmsApi.publishEvent(flagUnchanged(eventOnly(clean), original && normalizeBeforeSave(original)), draft.current, placeFaqsToSave(clean))
      onSaved(eventId ? 'Evento atualizado com sucesso.' : 'Evento publicado com sucesso.')
    } catch (error) {
      draftClosed.current = false
      onError(error instanceof Error ? error.message : 'Não foi possível salvar o evento.')
    } finally {
      setSaving(false)
    }
  }

  // An item fixed by hand returns to the automatic collection, which then
  // updates it again; the editor's other changes stay as they are.
  const followSource = async (kind: 'event' | 'run' | 'occurrence', id: string) => {
    if (!eventId) return
    if (!window.confirm('Na próxima coleta automática, o que foi editado à mão aqui será substituído pelo que a fonte informar. Continuar?')) return
    try {
      await cmsApi.followSource(eventId, kind, id)
      const release = (value: EventSnapshot): EventSnapshot => ({
        ...value,
        event: kind === 'event' ? { ...value.event, editorial_updated_at: null } : value.event,
        runs: value.runs.map((run) => ({
          ...run,
          editorial_updated_at: kind === 'run' && run.id === id ? null : run.editorial_updated_at,
          occurrences: run.occurrences.map((item) => kind === 'occurrence' && item.id === id ? { ...item, editorial_updated_at: null } : item),
        })),
      })
      setSnapshot((current) => current && release(current))
      setOriginal((current) => current && release(current))
      onNotice('Voltou a seguir a coleta automática.')
    } catch (error) {
      onError(error instanceof Error ? error.message : 'Não foi possível voltar a seguir a coleta.')
    }
  }

  // Each word one thing (PRD.md, item 73): an open event's changes are
  // discarded, back to how it opened; a new event's draft is deleted, closed
  // as discarded and kept, after the save under way.
  const discardChanges = () => {
    if (original && window.confirm('Descartar as alterações não salvas? O evento continua como está no app.')) setSnapshot(original)
  }
  const deleteDraft = async (then: () => void) => {
    draftClosed.current = true
    try {
      await drafting.current
      if (draft.current) {
        await cmsApi.discardDraft(draft.current)
        onNotice('Rascunho excluído.')
      }
    } catch (error) {
      draftClosed.current = false
      onError(error instanceof Error ? error.message : 'Não foi possível excluir o rascunho.')
      return
    }
    then()
  }

  // Saved by the button, it goes back to Eventos, where it waits in
  // Rascunhos, as Gmail closes a draft (PRD.md, item 43).
  const saveDraftNow = async (then: () => void = onBack) => {
    if (!(await saveDraft(snapshot, true))) return
    onNotice('Rascunho salvo.')
    then()
  }

  const draftLabel = draftState.status === 'saving' ? 'Salvando rascunho…'
    : draftState.status === 'error' ? 'Rascunho não salvo'
      : draftState.status === 'saved' && draftState.at ? `Rascunho salvo · ${formatWhen(draftState.at)}`
        : 'Alterações não salvas'

  const setWholeStatus = async (status: EventStatus) => {
    if (!eventId) {
      updateEvent('status', status)
      return
    }
    setSaving(true)
    try {
      await cmsApi.setEventStatus(eventId, status, `Alteração pelo CMS para ${status}`)
      setConfirming(null)
      onSaved(status === 'published' ? 'Evento republicado.' : status === 'canceled' ? 'Evento cancelado.' : 'Evento excluído do catálogo.')
    } catch (error) {
      onError(error instanceof Error ? error.message : 'Não foi possível alterar o status.')
    } finally {
      setSaving(false)
    }
  }

  const openItem = pending.find((item) => item.id === openPending)
  const askDiscard = (item: PendingCatalog) => {
    if (window.confirm(`Descartar ${item.value.name || (item.type === 'place' ? 'o lugar' : 'o perfil')} em criação? O que foi preenchido se perde.`)) discardPending(item.id)
  }
  const scopeOptions = <>
    <option value="event">Este evento, em todos os locais</option>
    {visibleRuns.map((run) => <option key={run.id} value={`run:${run.id}`}>Este evento, só em {run.place_name || 'local ainda não escolhido'}</option>)}
    {placeIds.map((placeId) => <option key={placeId} value={`place:${placeId}`}>Todos os eventos em {placeNameOf(placeId)}</option>)}
  </>
  const placeGroups = placeIds.map((placeId) => ({ placeId, faqs: shownFaqs(placeFaqsOf(placeId)) })).filter((group) => group.faqs.length)

  return (
    <div className={`page-stack editor-page ${reviewing ? 'editor-page--reviewing' : ''}`}>
      {leaving && (
        <LeaveDialog
          kind={eventId ? 'changes' : 'draft'}
          onDrop={eventId ? leaving : () => deleteDraft(leaving)}
          onKeep={() => saveDraftNow(leaving)}
          onClose={() => setLeaving(null)}
        />
      )}
      {reviewing && (
        <EventReview
          snapshot={normalizeBeforeSave(snapshot)}
          original={original && normalizeBeforeSave(original)}
          producers={bootstrap.producers}
          themes={bootstrap.themes}
          categories={bootstrap.categories}
          placeFaqs={{ changed: placeFaqsToSave(snapshot), saved: placeFaqsSaved, nameOf: placeNameOf }}
          saving={saving}
          onBack={backToForm}
          onConfirm={() => void save()}
        />
      )}
      <section className="editor-heading">
        <button className="icon-button" onClick={() => leave(onBack)} aria-label="Voltar"><ArrowLeft size={20} /></button>
        <div><h1>{eventId ? snapshot.event.title : copiedTitle ? 'Duplicar evento' : imported ? 'Revisar evento' : 'Criar evento'}</h1><p>De cima para baixo: o evento, onde e quando acontece, e as perguntas do público. Ao lado, como fica no app.</p></div>
        <div className="editor-heading__actions">
          {eventId && bootstrap.user.role === 'owner' && snapshot.event.status !== 'deleted' && <button className="button button--secondary" disabled={saving} onClick={() => setMerging(true)}><Merge size={17} /> Juntar duplicado</button>}
          {eventId && snapshot.event.status === 'published' && <button className="button button--danger" disabled={saving} onClick={() => setConfirming('canceled')}><XCircle size={17} /> Cancelar evento</button>}
          {eventId && snapshot.event.status !== 'published' && <button className="button button--secondary" disabled={saving} onClick={() => setConfirming('published')}><RotateCcw size={17} /> Republicar</button>}
          {eventId && snapshot.event.status !== 'deleted' && <button className="button button--danger" disabled={saving} onClick={() => setConfirming('deleted')}><Trash2 size={17} /> Excluir evento</button>}
          <button className="button button--primary" disabled={saving} onClick={review}><Save size={18} /> {saving ? 'Salvando…' : eventId ? 'Salvar evento' : 'Publicar evento'}</button>
        </div>
      </section>

      {imported && (
        <div className="notice" role="note"><Inbox size={16} /><span>
          Importado {imported.sourceUrl ? <>de <a href={imported.sourceUrl} target="_blank" rel="noreferrer">{imported.sourceUrl}</a></> : 'de um print'}, ainda fora do app. Confira tudo com a origem antes de publicar.
          {imported.warnings.length > 0 && <ul>{imported.warnings.map((warning) => <li key={warning}>{warning}</li>)}</ul>}
        </span></div>
      )}

      {copiedTitle && (
        <div className="notice" role="note"><Copy size={16} /><span>Cópia de <strong>{copiedTitle}</strong>, ainda fora do app. Mude o que for diferente e adicione as datas: os horários não vêm na cópia.</span></div>
      )}

      {openItem?.type === 'place' && (
        <PlaceQuickCreate
          value={openItem.value}
          draftId={openItem.draft_id}
          producers={bootstrap.producers}
          onChange={(value) => updatePending(openItem.id, value)}
          onMinimize={() => setOpenPending(null)}
          onDiscard={() => discardPending(openItem.id)}
          onCreated={(place, profile) => void placeCreated(openItem, place, profile)}
          onError={onError}
        />
      )}
      {openItem?.type === 'profile' && (
        <ProfileQuickCreate
          value={openItem.value}
          draftId={openItem.draft_id}
          onChange={(value) => updatePending(openItem.id, value)}
          onMinimize={() => setOpenPending(null)}
          onDiscard={() => discardPending(openItem.id)}
          onCreated={(profile) => void profileCreated(openItem, profile)}
          onError={onError}
        />
      )}

      {confirming && (
        <EventStatusDialog
          status={confirming}
          title={snapshot.event.title}
          unsaved={unsaved || linkPending}
          busy={saving}
          onConfirm={() => void setWholeStatus(confirming)}
          onClose={() => setConfirming(null)}
        />
      )}

      {merging && eventId && (
        <MergeDialog
          keepId={eventId}
          keepTitle={snapshot.event.title}
          catalogEditing={catalogEditing}
          onClose={() => setMerging(false)}
          onMerged={merged}
          onError={onError}
        />
      )}

      <div className="editor-layout">
      <div className="editor-main">
      <section className="panel form-section">
        <div className="form-section__heading"><span className="section-number">1</span><div><h2>Sobre o evento</h2><p>Informações compartilhadas por todos os locais e horários.</p></div>
          {isHandEdited(snapshot.event) && <EditedTag onFollow={() => void followSource('event', snapshot.event.id)} />}
        </div>
        <div className="form-grid">
          <label className="field field--wide"><span>Título *</span><input value={snapshot.event.title} onChange={(event) => updateEvent('title', event.target.value)} placeholder="Ex.: Hamilton — O Musical" /></label>
          <label className="field"><span>Categoria *</span><select value={snapshot.event.category} onChange={(event) => changeCategory(event.target.value)}>{bootstrap.categories.map((category) => <option key={category.name} value={category.name}>{category.display_name || category.name}</option>)}</select></label>
          {bootstrap.features?.event_themes && <label className="field"><span id="event-theme-label">Tema (opcional)</span>
            <select aria-labelledby="event-theme-label" value={snapshot.theme_slugs?.[0] ?? ''} disabled={saving} onChange={event => {
              const slug = event.target.value
              setSnapshot(current => current && { ...current, theme_slugs: slug ? [slug] : [] })
            }}>
              <option value="">Sem tema</option>
              {bootstrap.themes?.map(theme => <option key={theme.slug} value={theme.slug}>{theme.name}{!theme.is_active ? ' (inativo)' : ''}</option>)}
            </select>
          </label>}
          {isFestival && <div className="field field--wide" role="group" aria-labelledby="event-extra-categories-label">
            <span id="event-extra-categories-label">Também aparece em</span>
            <div className="choice-group choice-group--compact">
              {bootstrap.categories.filter((category) => category.slug !== FESTIVAL).map((category) => {
                const checked = snapshot.extra_category_slugs?.includes(category.slug) ?? false
                return <label className={`choice ${checked ? 'is-active' : ''}`} key={category.slug}>
                  <input type="checkbox" checked={checked} disabled={saving} onChange={() => toggleExtraCategory(category.slug)} />
                  <span><strong>{category.display_name || category.name}</strong></span>
                </label>
              })}
            </div>
            <small>Marque o que o festival tem na programação. Ele aparece também nos filtros dessas categorias, sempre como Festival.</small>
          </div>}
          <label className="field field--wide"><span>Descrição</span><textarea rows={6} value={snapshot.event.description} onChange={(event) => updateEvent('description', event.target.value)} placeholder="Conte ao público o que torna este evento especial." /></label>
          <label className="field"><span>Classificação etária</span><input type="number" min="0" step="1" disabled={snapshot.event.is_kid_friendly} value={snapshot.event.age_min ?? ''} onChange={(event) => updateEvent('age_min', event.target.value === '' ? null : Number(event.target.value))} placeholder="Não informada" /><small>{snapshot.event.is_kid_friendly
            ? 'Evento infantil é para todas as idades (0). Para mudar, desmarque Evento infantil.'
            : '0 é todas as idades; outro número, “A partir de N anos”. Vazio, o app não mostra.'}</small></label>
          {/* An event for children is for every age, unless the editor says otherwise after. */}
          <label className="toggle-field"><input type="checkbox" checked={snapshot.event.is_kid_friendly} onChange={(event) => { const kids = event.target.checked; setSnapshot((current) => current ? { ...current, event: { ...current.event, is_kid_friendly: kids, age_min: kids ? 0 : current.event.age_min } } : current) }} /><span><strong>Evento infantil</strong><small>Feito para crianças; a classificação passa a ser todas as idades. O app mostra "Evento infantil" nos destaques; não marque só por aceitar famílias.</small></span></label>
          <label className="toggle-field toggle-field--wide"><input type="checkbox" checked={snapshot.event.map_featured ?? false} onChange={(event) => updateEvent('map_featured', event.target.checked)} /><span><strong>Destaque no mapa</strong><small>Os locais deste evento aparecem primeiro no mapa do app, nos dias em que ele estiver no filtro. Marque poucos por cidade: com muitos, nenhum se destaca.</small></span></label>
          <div className="field field--wide"><span>Imagem do evento</span><div className="image-editor image-editor--source">
            <div className="image-editor__controls">
              <div className="mode-toggle"><button type="button" className={imageMode === 'link' ? 'is-active' : ''} onClick={() => setImageMode('link')}><Link2 size={15} /> Usar link</button><button type="button" className={imageMode === 'upload' ? 'is-active' : ''} onClick={() => setImageMode('upload')}><Upload size={15} /> Enviar arquivo</button></div>
              {imageMode === 'link' ? <ImageLinkInput label="Imagem do evento" link={snapshot.event.image_url_source} imageUrl={snapshot.event.image_url} prepareOnOpen={Boolean(draftId)} onImported={(preview, link, prepared) => {
                // The same link again (a draft opening) keeps the framing.
                if (link === snapshot.event.image_url_source) updateEvent('image_url', preview)
                else setImage(preview, link)
                setCover(prepared)
              }} onPendingChange={setLinkPending} onError={onError} /> : <label className="upload-drop"><Upload size={18} /><span>{uploading ? 'Preparando a imagem…' : uploadLabel || (snapshot.event.image_url ? 'Imagem armazenada. Escolha outra para substituir.' : 'JPG, PNG ou WEBP · até 8 MB')}</span><input type="file" accept="image/jpeg,image/png,image/webp" disabled={uploading} onChange={(event) => void uploadImage(event.target.files?.[0])} /></label>}
              <small>Na prévia ao lado, arraste a foto do card e a da página: cada uma tem o seu enquadramento.</small>
            </div>
          </div></div>
        </div>
      </section>

      <section className="form-section-heading">
        <div className="form-section__heading"><span className="section-number">2</span><div><h2>Locais e temporadas</h2><p>Cada bloco representa este evento em um mesmo local.</p></div></div>
        <button className="button button--secondary" onClick={addRun}><Plus size={17} /> Adicionar local</button>
      </section>

      <div className="run-stack">
        {visibleRuns.map((run, runIndex) => {
          const canceled = run.status === 'canceled'
          return (
            <section className={`panel run-card ${canceled ? 'run-card--canceled' : ''}`} key={run.id}>
              <div className="run-card__heading">
                <div><div><h3>Local {runIndex + 1}{canceled && <span className="status-pill status-pill--canceled">Cancelado aqui</span>}</h3><p>{run.place_name || 'Escolha onde o evento acontece'}</p></div></div>
                <div className="run-card__actions">
                  {isHandEdited(run) && <EditedTag onFollow={() => void followSource('run', run.id)} />}
                  <button className="text-button" onClick={() => toggleRunCanceled(run)}>{canceled ? <><RotateCcw size={16} /> Reativar neste local</> : <><Ban size={16} /> Cancelar neste local</>}</button>
                  <button className="text-button text-button--danger" onClick={() => removeRun(run)}><Trash2 size={16} /> Remover local</button>
                </div>
              </div>
              {canceled && <div className="notice notice--soft" role="note"><Ban size={16} /><span>Cancelado neste local: o app mostra os horários daqui como cancelados. Os outros locais seguem normais.</span></div>}
              <>
                <div className="form-grid form-grid--run">
                  <div className="field"><span>Local *</span><SuggestField
                    label="Local *"
                    value={bootstrap.places.find((place) => place.id === run.place_id)?.name ?? run.place_name}
                    placeholder="Digite o nome do local"
                    suggestions={placeSuggestions}
                    onPick={(placeId) => selectPlace(run.id, placeId)}
                    create={{ label: (text) => `Criar lugar “${text}”`, disabled: !catalogEditing, onCreate: (name) => createPlace(run.id, name) }}
                  />
                  {pending.filter((item) => item.type === 'place' && item.run_id === run.id).map((item) => (
                    <div className="pending-item" key={item.id} role="status">
                      <span><strong>{item.value.name || 'Lugar sem nome'}</strong> · em criação</span>
                      <button type="button" className="text-button" onClick={() => setOpenPending(item.id)}>Continuar</button>
                      <button type="button" className="text-button text-button--danger" onClick={() => askDiscard(item)}>Descartar</button>
                    </div>
                  ))}</div>
                  <div className="field"><span>Perfis</span>
                    <div className="credit-list">
                      {(run.credits ?? []).map((id) => {
                        const profile = bootstrap.producers.find((item) => item.id === id)
                        const name = profile?.name ?? 'Perfil removido'
                        return id === venueOf(run.place_id)?.id
                          ? <span className="credit-chip credit-chip--fixed" key={id}>{name}<small>do local</small></span>
                          : <span className="credit-chip" key={id}>{name}<button type="button" className="icon-button" aria-label={`Tirar ${name}`} onClick={() => removeProfile(run.id, id)}><X size={14} /></button></span>
                      })}
                      {pending.filter((item) => item.type === 'profile' && item.run_id === run.id).map((item) => (
                        <span className="credit-chip credit-chip--pending" key={item.id}>
                          {item.value.name || 'Perfil sem nome'}<small>em criação</small>
                          <button type="button" className="text-button" onClick={() => setOpenPending(item.id)}>Continuar</button>
                          <button type="button" className="icon-button" aria-label={`Descartar ${item.value.name || 'perfil'} em criação`} onClick={() => askDiscard(item)}><X size={14} /></button>
                        </span>
                      ))}
                      <SuggestField
                        label="Adicionar perfil"
                        value=""
                        placeholder="Adicionar perfil…"
                        suggestions={[...profileSuggestions.filter((item) => !(run.credits ?? []).includes(item.id)), ...draftSuggestions(catalogDrafts.producer)]}
                        onPick={(producerId) => addProfile(run.id, producerId)}
                        create={{ label: (text) => `Criar perfil “${text}”`, disabled: !catalogEditing, onCreate: (name) => createProfile(run.id, name) }}
                      />
                    </div>
                    {run.place_id && !venueOf(run.place_id) ? (
                      <div className="venue-link">
                        <small>Este local não tem perfil. Vincule o dele para ele entrar sozinho nos eventos daqui.</small>
                        <SuggestField
                          label="Vincular perfil do local"
                          value=""
                          placeholder={catalogEditing ? 'Vincular perfil do local…' : 'Vincular perfil do local (liga na virada)'}
                          disabled={!catalogEditing}
                          suggestions={profileSuggestions}
                          onPick={(producerId) => void linkVenue(run.id, run.place_id!, producerId)}
                        />
                      </div>
                    ) : <small>O perfil do local entra sozinho. O app mostra os perfis nesta ordem, na linha "Com…" acima das datas.</small>}
                  </div>
                  <label className="field"><span>Preço mínimo (R$)</span><div className="input-with-icon"><CircleDollarSign size={17} /><input type="number" min="0" step="0.01" disabled={run.is_free} value={run.price_min ?? ''} onChange={(event) => updateRun(run.id, { price_min: event.target.value === '' ? null : Number(event.target.value) })} placeholder="0,00" /></div><small>{run.is_free
                    ? 'Evento gratuito: o preço fica em 0. Para mudar, desmarque Evento gratuito.'
                    : 'Zero também é válido para lotes promocionais de eventos pagos.'}</small></label>
                  {/* Free is 0, locked; a paid event starts with no price, not "A partir de R$ 0" (PRD.md, item 15). */}
                  <label className="toggle-field"><input type="checkbox" checked={run.is_free} onChange={(event) => updateRun(run.id, { is_free: event.target.checked, price_min: event.target.checked ? 0 : null })} /><span><strong>Evento gratuito</strong><small>Marque somente quando o evento como um todo for gratuito.</small></span></label>
                  <label className="field"><span>Preço em texto</span><input value={run.price_label} onChange={(event) => updateRun(run.id, { price_label: event.target.value })} placeholder="Ex.: Couvert R$ 20 · Pague quanto puder" /><small>Opcional, para o que um número não diz. Substitui o “A partir de R$ …” ou o “Gratuito” na página do evento.</small></label>
                  <label className="field"><span>Link dos ingressos</span><input type="url" value={run.ticket_url_source ?? ''} onChange={(event) => updateRun(run.id, { ticket_url_source: event.target.value || null, ticket_url: event.target.value || null })} placeholder="https://…" /><small>O Guve acrescenta utm_source=guve, para o site de venda saber que a visita veio daqui.</small></label>
                  {run.is_free && <label className="field field--wide">
                    <span>Ingresso para eventos gratuitos</span>
                    <select
                      value={run.requires_ticket === true ? 'required' : run.requires_ticket === false ? 'not-required' : 'unknown'}
                      onChange={(event) => updateRun(run.id, {
                        requires_ticket: event.target.value === 'required' ? true : event.target.value === 'not-required' ? false : null,
                      })}
                    >
                      <option value="unknown">Não informado</option>
                      <option value="required">Precisa de ingresso</option>
                      <option value="not-required">Não precisa de ingresso</option>
                    </select>
                    <small>Nos horários sem link deste local, o app mostra “Ingressos no local” quando precisa de ingresso e “É só chegar!” nos demais, inclusive em “Não informado”. Marque sempre que houver retirada. Com link, oferece reserva, inscrição ou retirada online.</small>
                  </label>}
                </div>

                <RunSchedule
                  run={run}
                  city={bootstrap.places.find((place) => place.id === run.place_id)?.city}
                  savedSessions={savedSessions}
                  calendarAdds={calendarAdds}
                  onFollowSource={eventId ? (occurrenceId) => void followSource('occurrence', occurrenceId) : undefined}
                  onOccurrences={(occurrences) => setOccurrences(run.id, occurrences)}
                  onUpdateOccurrence={(occurrenceId, patch) => updateOccurrence(run.id, occurrenceId, patch)}
                />
              </>
            </section>
          )
        })}
      </div>

      <section className="form-section-heading">
        <div className="form-section__heading"><span className="section-number">3</span><div><h2>Perguntas e respostas</h2><p>Escolha se cada uma vale só para este evento, num local ou em todos, ou para todos os eventos de um local, como o dia gratuito de um museu.</p></div></div>
        <button className="button button--secondary" onClick={addFaq}><Plus size={17} /> Adicionar pergunta</button>
      </section>

      <div className="faq-stack">
        {visibleFaqs.length === 0 && !placeGroups.length ? (
          <div className="panel faq-empty"><CircleHelp size={22} /><div><strong>Nenhuma pergunta cadastrada</strong><span>Esta seção não aparecerá no app enquanto estiver vazia.</span></div></div>
        ) : visibleFaqs.map((faq, faqIndex) => (
          <section className="panel faq-card" key={faq.id}>
            <div className="faq-card__heading">
              <div><div><h3>Pergunta {faqIndex + 1}</h3><p>{faq.event_run_id ? 'Resposta específica de um local' : 'Resposta geral do evento'}</p></div></div>
              <div className="faq-card__actions">
                <button className="icon-button" disabled={faqIndex === 0} onClick={() => moveFaq(faq.id, -1)} aria-label="Mover pergunta para cima"><ArrowUp size={17} /></button>
                <button className="icon-button" disabled={faqIndex === visibleFaqs.length - 1} onClick={() => moveFaq(faq.id, 1)} aria-label="Mover pergunta para baixo"><ArrowDown size={17} /></button>
                <button className="text-button text-button--danger" onClick={() => removeFaq(faq.id)}><Trash2 size={16} /> Remover</button>
              </div>
            </div>
            <div className="form-grid faq-card__fields">
              <label className="field"><span>Vale para</span><select value={scopeOf(faq)} onChange={(event) => changeScope(faq, event.target.value)}>{scopeOptions}</select></label>
              <label className="field"><span>Pergunta *</span><input value={faq.question} onChange={(event) => updateFaq(faq.id, { question: event.target.value })} placeholder="Ex.: Preciso chegar com antecedência?" /></label>
              <label className="field field--wide"><span>Resposta *</span><textarea rows={4} value={faq.answer} onChange={(event) => updateFaq(faq.id, { answer: event.target.value })} placeholder="Escreva uma resposta curta e objetiva para o público." /></label>
            </div>
          </section>
        ))}
        {placeGroups.map(({ placeId, faqs }) => (
          <div className="faq-group" role="group" aria-label={`Perguntas de ${placeNameOf(placeId)}`} key={placeId}>
            <div className="faq-group__heading"><h3>Do local {placeNameOf(placeId)}</h3><p>Aparecem em todos os eventos deste local. Mudar aqui muda em todos, ao salvar o evento.</p></div>
            {faqs.map((faq, index) => (
              <section className="panel faq-card" key={faq.id}>
                <div className="faq-card__heading">
                  <div><div><h3>Pergunta do local {index + 1}</h3></div></div>
                  <div className="faq-card__actions">
                    <button className="icon-button" disabled={index === 0} onClick={() => movePlaceFaq(faq, -1)} aria-label="Mover pergunta do local para cima"><ArrowUp size={17} /></button>
                    <button className="icon-button" disabled={index === faqs.length - 1} onClick={() => movePlaceFaq(faq, 1)} aria-label="Mover pergunta do local para baixo"><ArrowDown size={17} /></button>
                    <button className="text-button text-button--danger" onClick={() => removePlaceFaq(faq)}><Trash2 size={16} /> Remover</button>
                  </div>
                </div>
                <div className="form-grid faq-card__fields">
                  <label className="field"><span>Vale para</span><select value={scopeOf(faq)} onChange={(event) => changeScope(faq, event.target.value)}>{scopeOptions}</select></label>
                  <label className="field"><span>Pergunta *</span><input value={faq.question} onChange={(event) => updatePlaceFaq(faq, { question: event.target.value })} placeholder="Ex.: Tem dia gratuito?" /></label>
                  <label className="field field--wide"><span>Resposta *</span><textarea rows={4} value={faq.answer} onChange={(event) => updatePlaceFaq(faq, { answer: event.target.value })} placeholder="Escreva uma resposta curta e objetiva para o público." /></label>
                </div>
              </section>
            ))}
          </div>
        ))}
      </div>
      </div>

      <EventPreview
        snapshot={snapshot}
        disabled={saving}
        onFocus={(crop, focus) => setSnapshot((current) => current ? {
          ...current,
          event: { ...current.event, ...(crop === 'card'
            ? { image_focus_x: focus?.x ?? null, image_focus_y: focus?.y ?? null }
            : { image_page_focus_x: focus?.x ?? null, image_page_focus_y: focus?.y ?? null }) },
        } : current)}
      />
      </div>

      {(unsaved || linkPending) && (
        <div className="editor-footer" role="region" aria-label="Alterações não salvas">
          <span role="status">{eventId ? 'Alterações não salvas' : draftLabel}</span>
          <div>
            {eventId
              ? <button className="button button--secondary" disabled={saving} onClick={discardChanges}>Descartar alterações</button>
              : <button className="button button--secondary" disabled={saving} onClick={() => { if (window.confirm('Excluir este rascunho? Ele sai da lista de rascunhos.')) void deleteDraft(onBack) }}>Excluir rascunho</button>}
            {!eventId && <button className="button button--secondary" disabled={saving || draftState.status === 'saving'} onClick={() => void saveDraftNow()}><FilePen size={17} /> Salvar rascunho</button>}
            <button className="button button--primary" disabled={saving} onClick={review}><Save size={18} /> {saving ? 'Salvando…' : eventId ? 'Salvar evento' : 'Publicar evento'}</button>
          </div>
        </div>
      )}
    </div>
  )
}
